import { RuleId, staticAltTextRule } from '@wix/a11y-rules';
import { ITEM_TYPES } from '@wix/advanced-seo-utils';
import { useCallback, useMemo } from 'react';
import { convertBooleanStatusToElementStatus, convertScanStatusToUiStatus } from '../../../components/fixers/alt-image-fixer/utils';
import { usePanelStateAtom } from '../../../hooks/rules-state';
import { useA11yWizardAdapter } from '../../../hooks/use-a11y-wizard-adapter';
import { getAxeBreakpointId } from '../../../utils/get-axe-breakpoint-id';
import { getIsAiSupported } from '../../../utils/get-is-ai-supported';
import { getUpdatedAltTextImagesFromState } from '../alt-text/get-updated-alt-text-images-from-state';
export const useBaseAltTextRuleHandlers = () => {
  const {
    a11yWizardAdapter,
    editorHost
  } = useA11yWizardAdapter();
  const {
    getPagesData,
    panelState,
    getRuleState
  } = usePanelStateAtom();
  const pageIdToPageData = getPagesData();
  const getRuleId = useCallback(ruleExecutionResult => {
    var _ruleExecutionResult$;
    return ((_ruleExecutionResult$ = ruleExecutionResult[0]) == null ? void 0 : _ruleExecutionResult$.ruleId) ?? 'UNKNOWN_RULE_ID';
  }, []);
  const getIsNotApplicable = useCallback(ruleExecutionResults => {
    return ruleExecutionResults.every(_ref => {
      let {
        result
      } = _ref;
      return result == null || result.status === 'not-applicable';
    });
  }, []);
  const extractAltImageProps = useCallback((ruleExecutionResults, pageId) => {
    var _pageIdToPageData$get, _ruleState$state;
    const ruleState = getRuleState(pageId, RuleId.STATIC_ALT_TEXT);
    const innerRoutes = (_pageIdToPageData$get = pageIdToPageData.get(pageId)) == null ? void 0 : _pageIdToPageData$get.innerRoutes;
    const failedElementIdsSet = new Set();
    const altImageProps = [];
    ruleExecutionResults.forEach(_ref2 => {
      let {
        result,
        context,
        metadata
      } = _ref2;
      if (innerRoutes === undefined || innerRoutes.find(route => (metadata == null ? void 0 : metadata.pageId) === route.id) !== undefined || (metadata == null ? void 0 : metadata.pageId) === pageId) {
        const failedElements = result == null ? void 0 : result.failedElements;
        const entityId = metadata == null ? void 0 : metadata.pageId;
        context == null || context.images.forEach(image => {
          const {
            id,
            alt,
            decorative,
            isLinked,
            uri,
            tpaData,
            builderLabel,
            componentId,
            enableDecorativeImages
          } = image;
          if (!failedElementIdsSet.has(id)) {
            failedElementIdsSet.add(id);
            const isLinkedImage = !!isLinked;
            altImageProps.push({
              alt,
              breakpointId: getAxeBreakpointId(a11yWizardAdapter, editorHost, metadata, componentId ?? (tpaData == null ? void 0 : tpaData.componentId) ?? id),
              builderLabel,
              componentId,
              entityId,
              id,
              imageEnableDecorativeImages: enableDecorativeImages,
              isAiSupported: getIsAiSupported(uri),
              isDecorative: !!decorative && !alt,
              isLinked: isLinkedImage,
              status: convertBooleanStatusToElementStatus(failedElements.find(failedElement => failedElement.id === id) === undefined),
              tpaData,
              url: uri
            });
          }
        });
      }
    });
    return getUpdatedAltTextImagesFromState(altImageProps, ruleState == null || (_ruleState$state = ruleState.state) == null ? void 0 : _ruleState$state.savedImages, panelState.globalSectionComponentsDataMap);
  }, [getRuleState, pageIdToPageData, panelState.globalSectionComponentsDataMap, a11yWizardAdapter, editorHost]);
  const getRuleStatus = useCallback((ruleExecutionResults, pageId) => {
    var _ruleState$state4;
    const isOneFailed = ruleExecutionResults.some(_ref3 => {
      let {
        result
      } = _ref3;
      return (result == null ? void 0 : result.status) === 'failed';
    });
    const initialStatus = isOneFailed ? 'failed' : 'passed';
    const initialAltRuleProps = extractAltImageProps(ruleExecutionResults, pageId);
    const ruleState = getRuleState(pageId, RuleId.STATIC_ALT_TEXT);
    const altToChecker = [...initialAltRuleProps].map(image => ({
      ...image,
      decorative: image.isDecorative
    }));
    const hasGlobalSectionChanges = panelState.globalSectionComponentsDataMap && panelState.globalSectionComponentsDataMap.size > 0;
    const stateStatus = hasGlobalSectionChanges ? convertScanStatusToUiStatus(staticAltTextRule.checker({
      images: altToChecker.map(image => {
        var _panelState$globalSec, _ruleState$state2, _panelState$globalSec2, _ruleState$state3;
        return {
          ...image,
          alt: ((_panelState$globalSec = panelState.globalSectionComponentsDataMap) == null || (_panelState$globalSec = _panelState$globalSec.get(image.id)) == null ? void 0 : _panelState$globalSec.alt) ?? (ruleState == null || (_ruleState$state2 = ruleState.state) == null || (_ruleState$state2 = _ruleState$state2.savedImages) == null || (_ruleState$state2 = _ruleState$state2.get(image.id)) == null ? void 0 : _ruleState$state2.alt) ?? image.alt ?? '',
          decorative: ((_panelState$globalSec2 = panelState.globalSectionComponentsDataMap) == null || (_panelState$globalSec2 = _panelState$globalSec2.get(image.id)) == null ? void 0 : _panelState$globalSec2.isDecorative) ?? (ruleState == null || (_ruleState$state3 = ruleState.state) == null || (_ruleState$state3 = _ruleState$state3.savedImages) == null || (_ruleState$state3 = _ruleState$state3.get(image.id)) == null ? void 0 : _ruleState$state3.isDecorative) ?? image.isDecorative,
          uri: image.url
        };
      })
    }).status) : ruleState == null || (_ruleState$state4 = ruleState.state) == null ? void 0 : _ruleState$state4.status;
    const uiStatus = stateStatus ?? convertScanStatusToUiStatus(stateStatus ?? initialStatus);
    return uiStatus;
  }, [extractAltImageProps, getRuleState, panelState.globalSectionComponentsDataMap]);
  const getGreenStateDescriptionKey = useCallback(itemType => {
    switch (itemType) {
      case ITEM_TYPES.RESTAURANTS_MENU_PAGE:
        return 'accessibility-wizard.rules-list.alt-image-rule-fixer.restaurants-menu-fixer';
      default:
        return 'accessibility-wizard.rules-list.alt-image-rule-fixer.verticals.no-images-green-state.description';
    }
  }, []);
  return useMemo(() => ({
    extractAltImageProps,
    getGreenStateDescriptionKey,
    getIsNotApplicable,
    getRuleId,
    getRuleStatus
  }), [getIsNotApplicable, getRuleId, getRuleStatus, extractAltImageProps, getGreenStateDescriptionKey]);
};
//# sourceMappingURL=use-base-alt-text-rule-handlers.js.map